import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, LogIn, UserPlus } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import axiosInstance from "@/services/axios";
import { API } from "@/config/api";

// const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BACKEND_URL = "http://127.0.0.1:8000";
const API_URL = `${BACKEND_URL}/api/v1`;

export const CheckoutPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [cartItems, setCartItems] = useState([]);
  const [shippingMethods, setShippingMethods] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showAuthPrompt, setShowAuthPrompt] = useState(true);

  const [billingAddress, setBillingAddress] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address: "",
    city: "",
    state: "",
    zipCode: "",
    country: "US",
  });

  const [shippingAddress, setShippingAddress] = useState({ ...billingAddress });
  const [sameAsBilling, setSameAsBilling] = useState(true);
  const [selectedShipping, setSelectedShipping] = useState("");
  const [orderNotes, setOrderNotes] = useState("");
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (sameAsBilling) {
      setShippingAddress({ ...billingAddress });
    }
  }, [sameAsBilling, billingAddress]);

  // Auto-fill email if user is logged in
  useEffect(() => {
    if (isAuthenticated && user?.email) {
      setBillingAddress((prev) => ({
        ...prev,
        email: user.email,
        firstName: user.first_name || prev.firstName,
        lastName: user.last_name || prev.lastName,
        phone: user.phone || prev.phone,
      }));
    }
  }, [isAuthenticated, user]);

  const loadData = async () => {
    setLoading(true);

    try {
      let cart = [];

      console.log("🔍 STEP 5 - Loading checkout data");
      console.log("🔍 STEP 5 - Is authenticated:", isAuthenticated);

      // If authenticated, load cart from server first
      if (isAuthenticated) {
        try {
          console.log("🔍 STEP 5 - Fetching cart from server...");
          const response = await axiosInstance.get(API.auth.cart.get);
          console.log("🔍 STEP 5 - Server cart response:", response.data);
          cart = response.data.items || [];

          // Also update localStorage with server cart
          localStorage.setItem("cart", JSON.stringify(cart));
          console.log("🔍 STEP 5 - Updated localStorage with server cart");
        } catch (error) {
          console.error("🔍 STEP 5 - Failed to load cart from server:", error);
          cart = JSON.parse(localStorage.getItem("cart") || "[]");
          console.log("🔍 STEP 5 - Fallback to localStorage cart:", cart);
        }
      } else {
        // Guest user - load from localStorage
        cart = JSON.parse(localStorage.getItem("cart") || "[]");
        console.log("🔍 STEP 5 - Guest cart from localStorage:", cart);
      }

      console.log("🔍 STEP 5 - Final cart for checkout:", cart);

      if (cart.length === 0) {
        console.log("🔍 STEP 5 - Cart empty, redirecting to cart page");
        toast.error("Your cart is empty");
        navigate("/cart");
        return;
      }

      setCartItems(cart);

      // Load shipping methods
      const response = await axios.get(`${API_URL}/shipping/methods`);
      setShippingMethods(response.data);
      if (response.data.length > 0) {
        setSelectedShipping(response.data[2].id);
      }
    } catch (error) {
      console.error("🔍 STEP 5 - Error loading checkout data:", error);
      toast.error("Failed to load checkout data");
    } finally {
      setLoading(false);
    }
  };

  const handleBillingChange = (field, value) => {
    setBillingAddress((prev) => ({ ...prev, [field]: value }));
  };

  const handleShippingChange = (field, value) => {
    setShippingAddress((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    const required = [
      "firstName",
      "lastName",
      "email",
      "address",
      "city",
      "state",
      "zipCode",
    ];
    for (const field of required) {
      if (!billingAddress[field]) {
        toast.error(`Please fill in all required fields`);
        return false;
      }
    }
    if (!disclaimerAccepted) {
      toast.error("You must accept the casting disclaimer to proceed");
      return false;
    }
    if (!selectedShipping) {
      toast.error("Please select a shipping method");
      return false;
    }
    return true;
  };

  const mapAddress = (addr) => ({
    address_line1: addr.address,
    city: addr.city,
    state: addr.state,
    zip_code: addr.zipCode,
    country: addr.country || "US",
    phone: addr.phone || undefined,
  });

  const handlePlaceOrder = async () => {
    if (!validateForm()) return;

    setProcessing(true);

    try {
      const orderData = {
        items: cartItems,
        shipping_method_id: selectedShipping,
        billing_address: mapAddress(billingAddress),
        shipping_address: sameAsBilling ? mapAddress(billingAddress) : mapAddress(shippingAddress),
        contact_email: billingAddress.email,
        order_notes: orderNotes,
      };

      const orderResponse = await axios.post(`${API_URL}/orders`, orderData);
      const orderId = orderResponse.data.id;

      localStorage.removeItem("cart");
      window.dispatchEvent(new Event("storage"));
      navigate(`/order-confirmation?order_id=${orderId}`);
    } catch (error) {
      console.error("Error creating order:", error);
      const detail = error.response?.data?.detail;
      toast.error(Array.isArray(detail) ? detail.map((e) => `${e.loc?.at(-1) ?? "field"}: ${e.msg}`).join(", ") : detail || "Failed to process order");
      setProcessing(false);
    }
  };

  const subtotal = cartItems.reduce((sum, item) => sum + item.line_total, 0);
  const shippingCost =
    shippingMethods.find((m) => m.id === selectedShipping)?.price || 0;
  const total = subtotal + shippingCost;

  if (loading) {
    return (
      <div className="min-h-screen bg-background py-24 px-6 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-24 px-6">
      <div className="max-w-6xl mx-auto">
        <h1
          className="font-heading text-4xl font-bold mb-8"
          data-testid="checkout-title"
        >
          Checkout
        </h1>

        {/* Auth Prompt Banner */}
        {!isAuthenticated && showAuthPrompt && (
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-md p-6 mb-8">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h3 className="font-semibold text-lg text-blue-900 mb-1">
                  🚀 Faster Checkout with an Account
                </h3>
                <p className="text-sm text-blue-700">
                  Sign in to auto-fill your details, track orders, and save your
                  address for next time!
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={() =>
                    navigate("/login", { state: { from: "/checkout" } })
                  }
                  variant="outline"
                  className="border-blue-300 text-blue-700 hover:bg-blue-100"
                >
                  <LogIn className="w-4 h-4 mr-2" />
                  Sign In
                </Button>
                <Button
                  onClick={() =>
                    navigate("/register", { state: { from: "/checkout" } })
                  }
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Create Account
                </Button>
                <button
                  onClick={() => setShowAuthPrompt(false)}
                  className="text-blue-400 hover:text-blue-600"
                  aria-label="Close"
                >
                  ✕
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Logged in user info */}
        {isAuthenticated && (
          <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-8">
            <p className="text-green-800 text-sm">
              <span className="font-semibold">Logged in as:</span> {user?.email}{" "}
              •<span className="ml-1">Cart has {cartItems.length} item(s)</span>
            </p>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-white border border-border p-6 rounded-md">
              <h2 className="font-heading font-semibold text-xl mb-6">
                Billing Information
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName">First Name *</Label>
                  <input
                    id="firstName"
                    type="text"
                    value={billingAddress.firstName}
                    onChange={(e) =>
                      handleBillingChange("firstName", e.target.value)
                    }
                    className="w-full border border-input rounded-sm px-4 py-3 mt-2"
                    data-testid="billing-first-name"
                  />
                </div>
                <div>
                  <Label htmlFor="lastName">Last Name *</Label>
                  <input
                    id="lastName"
                    type="text"
                    value={billingAddress.lastName}
                    onChange={(e) =>
                      handleBillingChange("lastName", e.target.value)
                    }
                    className="w-full border border-input rounded-sm px-4 py-3 mt-2"
                    data-testid="billing-last-name"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="email">Email *</Label>
                  <input
                    id="email"
                    type="email"
                    value={billingAddress.email}
                    onChange={(e) =>
                      handleBillingChange("email", e.target.value)
                    }
                    className="w-full border border-input rounded-sm px-4 py-3 mt-2"
                    data-testid="billing-email"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="phone">Phone</Label>
                  <input
                    id="phone"
                    type="tel"
                    value={billingAddress.phone}
                    onChange={(e) =>
                      handleBillingChange("phone", e.target.value)
                    }
                    className="w-full border border-input rounded-sm px-4 py-3 mt-2"
                    data-testid="billing-phone"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="address">Street Address *</Label>
                  <input
                    id="address"
                    type="text"
                    value={billingAddress.address}
                    onChange={(e) =>
                      handleBillingChange("address", e.target.value)
                    }
                    className="w-full border border-input rounded-sm px-4 py-3 mt-2"
                    data-testid="billing-address"
                  />
                </div>
                <div>
                  <Label htmlFor="city">City *</Label>
                  <input
                    id="city"
                    type="text"
                    value={billingAddress.city}
                    onChange={(e) =>
                      handleBillingChange("city", e.target.value)
                    }
                    className="w-full border border-input rounded-sm px-4 py-3 mt-2"
                    data-testid="billing-city"
                  />
                </div>
                <div>
                  <Label htmlFor="state">State *</Label>
                  <input
                    id="state"
                    type="text"
                    value={billingAddress.state}
                    onChange={(e) =>
                      handleBillingChange("state", e.target.value)
                    }
                    className="w-full border border-input rounded-sm px-4 py-3 mt-2"
                    data-testid="billing-state"
                  />
                </div>
                <div>
                  <Label htmlFor="zipCode">ZIP Code *</Label>
                  <input
                    id="zipCode"
                    type="text"
                    value={billingAddress.zipCode}
                    onChange={(e) =>
                      handleBillingChange("zipCode", e.target.value)
                    }
                    className="w-full border border-input rounded-sm px-4 py-3 mt-2"
                    data-testid="billing-zip"
                  />
                </div>
              </div>
            </div>

            <div className="bg-white border border-border p-6 rounded-md">
              <div className="flex items-center gap-3 mb-6">
                <Checkbox
                  id="sameAsBilling"
                  checked={sameAsBilling}
                  onCheckedChange={setSameAsBilling}
                  data-testid="same-as-billing-checkbox"
                />
                <Label htmlFor="sameAsBilling" className="cursor-pointer">
                  Shipping address same as billing
                </Label>
              </div>

              {!sameAsBilling && (
                <div>
                  <h2 className="font-heading font-semibold text-xl mb-6">
                    Shipping Information
                  </h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>First Name *</Label>
                      <input
                        type="text"
                        value={shippingAddress.firstName}
                        onChange={(e) =>
                          handleShippingChange("firstName", e.target.value)
                        }
                        className="w-full border border-input rounded-sm px-4 py-3 mt-2"
                      />
                    </div>
                    <div>
                      <Label>Last Name *</Label>
                      <input
                        type="text"
                        value={shippingAddress.lastName}
                        onChange={(e) =>
                          handleShippingChange("lastName", e.target.value)
                        }
                        className="w-full border border-input rounded-sm px-4 py-3 mt-2"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label>Street Address *</Label>
                      <input
                        type="text"
                        value={shippingAddress.address}
                        onChange={(e) =>
                          handleShippingChange("address", e.target.value)
                        }
                        className="w-full border border-input rounded-sm px-4 py-3 mt-2"
                      />
                    </div>
                    <div>
                      <Label>City *</Label>
                      <input
                        type="text"
                        value={shippingAddress.city}
                        onChange={(e) =>
                          handleShippingChange("city", e.target.value)
                        }
                        className="w-full border border-input rounded-sm px-4 py-3 mt-2"
                      />
                    </div>
                    <div>
                      <Label>State *</Label>
                      <input
                        type="text"
                        value={shippingAddress.state}
                        onChange={(e) =>
                          handleShippingChange("state", e.target.value)
                        }
                        className="w-full border border-input rounded-sm px-4 py-3 mt-2"
                      />
                    </div>
                    <div>
                      <Label>ZIP Code *</Label>
                      <input
                        type="text"
                        value={shippingAddress.zipCode}
                        onChange={(e) =>
                          handleShippingChange("zipCode", e.target.value)
                        }
                        className="w-full border border-input rounded-sm px-4 py-3 mt-2"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white border border-border p-6 rounded-md">
              <h2 className="font-heading font-semibold text-xl mb-6">
                Shipping Method
              </h2>
              <div className="space-y-2">
                {shippingMethods.map((method) => (
                  <label
                    key={method.id}
                    className="flex items-center gap-3 p-3 border border-border rounded-sm cursor-pointer hover:border-accent transition-colors"
                  >
                    <input
                      type="radio"
                      name="shipping"
                      value={method.id}
                      checked={selectedShipping === method.id}
                      onChange={(e) => setSelectedShipping(e.target.value)}
                      className="text-accent"
                      data-testid={`checkout-shipping-${method.id}`}
                    />
                    <div className="flex-1">
                      <div className="font-medium text-sm">{method.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {method.estimated_days}
                      </div>
                    </div>
                    <div className="font-mono text-sm">
                      ${method.price.toFixed(2)}
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="bg-white border border-border p-6 rounded-md">
              <Label htmlFor="orderNotes">Order Notes (Optional)</Label>
              <textarea
                id="orderNotes"
                value={orderNotes}
                onChange={(e) => setOrderNotes(e.target.value)}
                rows={4}
                className="w-full border border-input rounded-sm px-4 py-3 mt-2"
                placeholder="Any special instructions for your order..."
                data-testid="order-notes"
              />
            </div>

            <div className="bg-amber-50 border border-amber-200 p-6 rounded-md">
              <h3 className="font-semibold mb-4">Casting Disclaimer</h3>
              <div
                className="text-sm text-muted-foreground space-y-2 max-h-48 overflow-y-auto mb-4 pr-2"
                data-testid="disclaimer-text"
              >
                <p>I understand and agree that:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>
                    Models must be manifold, watertight, and casting-ready
                  </li>
                  <li>Minimum recommended wall thickness: 0.8-1.0 mm</li>
                  <li>Thin/prong areas risk failure during casting</li>
                  <li>
                    Final surface finish depends on model quality & requires
                    cleanup/polish (unless purchased)
                  </li>
                  <li>
                    Recasts/refunds only for proven casting defects (not design
                    issues)
                  </li>
                  <li>Report issues within 72 hours of receipt</li>
                  <li>
                    Precious metal weights are estimates — final weight may vary
                    ±5-10%
                  </li>
                </ul>
              </div>
              <div className="flex items-start gap-3">
                <Checkbox
                  id="disclaimer"
                  checked={disclaimerAccepted}
                  onCheckedChange={setDisclaimerAccepted}
                  data-testid="disclaimer-checkbox"
                />
                <Label htmlFor="disclaimer" className="cursor-pointer text-sm">
                  I have read and accept the casting disclaimer *
                </Label>
              </div>
            </div>
          </div>

          <div>
            <div className="bg-white border border-border p-6 rounded-md sticky top-24">
              <h3 className="font-heading font-semibold text-xl mb-6">
                Order Summary
              </h3>

              <div className="space-y-3 mb-6">
                <div className="text-sm">
                  <span className="text-muted-foreground">
                    {cartItems.length} item(s)
                  </span>
                </div>
                {cartItems.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span>
                      {item.filename} ({item.metal_name}) x{item.quantity}
                    </span>
                    <span className="font-mono">
                      ${item.line_total.toFixed(2)}
                    </span>
                  </div>
                ))}
                <div className="flex justify-between text-sm pt-2 border-t border-border">
                  <span>Subtotal:</span>
                  <span className="font-mono" data-testid="checkout-subtotal">
                    ${subtotal.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Shipping:</span>
                  <span
                    className="font-mono"
                    data-testid="checkout-shipping-cost"
                  >
                    ${shippingCost.toFixed(2)}
                  </span>
                </div>
                <div className="pt-3 border-t border-border flex justify-between">
                  <span className="font-semibold">Total:</span>
                  <span
                    className="font-heading font-bold text-2xl text-accent"
                    data-testid="checkout-total"
                  >
                    ${total.toFixed(2)}
                  </span>
                </div>
              </div>

              <Button
                onClick={handlePlaceOrder}
                disabled={processing || !disclaimerAccepted}
                className="w-full bg-accent hover:bg-accent-hover text-white px-8 py-6 text-sm uppercase tracking-widest"
                data-testid="place-order-button"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  "Place Order"
                )}
              </Button>

              <p className="text-xs text-muted-foreground text-center mt-4">
                Payments processed securely — we never store card data
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
